import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbTabsModule } from 'mdb-angular-ui-kit/tabs';
import { MdbScrollspyModule } from 'mdb-angular-ui-kit/scrollspy';
import { MdbDropdownModule } from 'mdb-angular-ui-kit/dropdown';
import { HttpClient } from '@angular/common/http';
import { SaveModalPopupComponent } from '../save-modal-popup/save-modal-popup.component';
import { UserService } from '../../shared/services/user.service';
import { AppraisalDataService } from '../../shared/services/appraisal-data.service';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridModule } from 'ag-grid-angular';
import { environment } from '../../../environments/environment';
import { ActivatedRoute } from '@angular/router';
import { SectionService } from '../../shared/services/section.service';
import { ArgusDataService } from '../../shared/services/argus-data.service';
import { Argus, Analysis, Assumptions, CashFlow, Common, ContractMonthRent, ExpenseInflationRate, ImprovementInfo, LeaseExpirations, LocationInfo, MarketInflationRate, NetOperatingIncome, PropertyOverview, RentRollforWALT, ValuationSummary } from '../../models/argus';

@Component({
  selector: 'app-argus',
  standalone: true,
  imports: [CommonModule, AgGridModule, MatTabsModule, MdbFormsModule, MdbTabsModule, MdbScrollspyModule, MdbDropdownModule, 
    SaveModalPopupComponent],
  templateUrl: './argus.component.html',
  styleUrl: './argus.component.scss'
})

export class ArgusComponent implements OnInit {
  @Input() gridTheme: string = 'ag-theme-quartz';
  @Input() isDarkMode: boolean = false; 
  
  activityNumber: string | null | undefined;
  amountFormat: string = "currency:'USD':true:'1.0-0'";
  argusResponse: any = {};  // This will receive the data for Argus
  errorMessages: any[] = [];  
  inputParams: any = {};
  isLoading: boolean = false;
  model: any = {};
  themeClass = `${this.gridTheme}${this.isDarkMode ? '-dark' : ''}`;
  userData: any = {};
  warningMessages: any[] = [];
  argusViewModel: Argus = {};

  defaultColDef: any = {
    resizable: true,
    sortable: true,
    filter: true,
    wrapText: true,
    autoHeight: true
  }; 

  inflationRateColumnDefs: any[] = [
    { headerName: 'Year Ending', field: 'month', pinned: 'left', maxWidth: 120, autoHeight: true,
            wrapHeaderText: true }
  ];

  cashFlowColumnDefs: any[] = [
    { headerName: 'Cash Flow Item', field: 'cashflow', pinned: 'left', maxWidth: 180 }
  ];

  leaseExpirationColumnDefs: any[] = [
    {
      headerName: 'Year 1', field: 'year 1', minWidth: 70, maxWidth: 120, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: 'Year 2', field: 'year 2', minWidth: 70, maxWidth: 120, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: 'Year 3', field: 'year 3', minWidth: 70, maxWidth: 120, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: 'Year 4', field: 'year 4', minWidth: 70, maxWidth: 120,alueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: 'Year 5', field: 'year 5', minWidth: 70, maxWidth: 120, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: '5 Year Total', field: '5yeartotal', minWidth: 80, maxWidth: 130, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell',
      autoHeight: true,
      wrapHeaderText: true
    }
  ];

  wALTColumnDefs: any[] = [
    { headerName: 'Tenant Name', field: 'name', pinned: 'left', autoHeight: true,
      wrapText: true, resizable: true },
    { headerName: 'Suite', field: 'suite', autoHeight: true,
      wrapText: true },
    {
      headerName: 'Area', field: 'area', valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: 'Lease\nExpiry', field: 'leaseEndDate',  valueFormatter: (params: { value: string | number | Date; }) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US');
      },
      autoHeight: true,
      wrapText: true,
      cellStyle: { 'white-space': 'normal', 'text-align': 'center' },
      headerClass: 'multi-line-header'
    },
    {
      headerName: 'Lease Status', field: 'leaseStatusBasis', valueFormatter: (paramStatus: { value: number; }) => {
        return LeaseStatus[paramStatus.value];
      }
    }
  ];

  leasingAssumptionsColumnDefs: any[] = [
    { headerName: 'Market\nLeasing\nName', field: 'marketLeaseName', pinned: 'left', minWidth: 330, autoHeight: true,
      wrapText: true, resizable: true },    
    {
      headerName: 'Term\n(Years)', field: 'leaseTermYears', maxWidth: 160, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell', 
        autoHeight: true,
        wrapHeaderText: true
    },
    {
      headerName: 'Renewal\nPercent', field: 'renewalPercent', maxWidth: 140, valueFormatter: (params: { value: any; }) => {
              const value = Number(params.value);
              if (isNaN(value)) return '';
              return (value * 100) + '%';
            }, cellClass: 'ag-right-aligned-cell',
        autoHeight: true,
        wrapHeaderText: true
    },    
    {
      headerName: 'Months\nVacant', field: 'monthsVacant', maxWidth: 140, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell',      
        autoHeight: true,
        wrapHeaderText: true
    },
    {
      headerName: 'Market\nRent', field: 'marketRent', maxWidth: 140, valueFormatter: (params: { value: any; }) => {
        const val = Number(params.value);
        if (isNaN(val)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(val);}, cellClass: 'ag-right-aligned-cell', 
        autoHeight: true,
        wrapHeaderText: true    
    },
    {
      headerName: 'Free\nRent\n(Months)', field: 'freeRentMonths', maxWidth: 140, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell',
      autoHeight: true,
      wrapHeaderText: true
    },
    {
      headerName: 'Tenant\nImprovements', field: 'tenantImprovementNew', maxWidth: 180, valueFormatter: (params: { value: any; }) => {
        const val = Number(params.value);
        if (isNaN(val)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(val);}, cellClass: 'ag-right-aligned-cell', 
        autoHeight: true,
        wrapHeaderText: true
    },
    {
      headerName: 'Leasing\nCommissions', field: 'leaseCommissionsNew', maxWidth: 180, valueFormatter: (params: { value: any; }) => {
              const value = Number(params.value);
              if (isNaN(value)) return '';
              return (value * 100) + '%';
            }, cellClass: 'ag-right-aligned-cell',
        autoHeight: true,
        wrapHeaderText: true
    }   
  ];

  contractRentColumnDefs: any[] = [
    { headerName: 'Tenant Name', field: 'name', pinned: 'left', maxWidth: 430, autoHeight: true,
      wrapText: true, resizable: true },
    { headerName: 'Suite', field: 'suite', maxWidth: 120, autoHeight: true,
      wrapText: true },
    {
      headerName: 'Area', field: 'area', maxWidth: 150, valueFormatter: (params: { value: { toLocaleString: () => any; } | null; }) => {
        if (params.value == null) return '';
        return params.value.toLocaleString();
      }, cellClass: 'ag-right-aligned-cell'
    },
    {
      headerName: 'Contract Rent\nYearly', field: 'contractYearlyRent', maxWidth: 180, valueFormatter: (params: { value: any; }) => {
        const val = Number(params.value);
        if (isNaN(val)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(val);}, cellClass: 'ag-right-aligned-cell', 
        autoHeight: true,
        wrapHeaderText: true
    },
    {
      headerName: 'Market\nRent\nYearly\nPSF', field: 'marketRent', maxWidth: 180, valueFormatter: (params: { value: any; }) => {
        const val = Number(params.value);
        if (isNaN(val)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(val);}, cellClass: 'ag-right-aligned-cell',
        autoHeight: true,
        wrapHeaderText: true
    }
  ];

  sectionList = [
    { id: 'Property-Overview', label: 'Property Overview' },
    { id: 'Location-Info', label: 'Location Information' },   
    { id: 'Analysis', label: 'Analysis' }, 
    { id: 'Improvement-Info', label: 'Improvement Information' }, 
    { id: 'Cashflow', label: 'Cash Flow' },   
    { id: 'Assumptions', label: 'Assumptions' },
    { id: 'Lease-Expirations', label: 'Lease Expirations' },
    { id: 'Leasing-Assumptions', label: 'Leasing Assumptions' },
    { id: 'Contract-Month-Rent', label: 'Contract Month Rent' },
    { id: 'Valuation-Summary', label: 'Valuation Summary' }
  ];

  constructor(private http: HttpClient, private route: ActivatedRoute,private sectionService: SectionService,  private appraisalDataService: AppraisalDataService, private userService: UserService, private argusDataService: ArgusDataService) {
    LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-060398}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Cushman_&_Wakefield_Global,_Inc}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{18_June_2025}____[v3]_[0102]_MTc1MDIwMTIwMDAwMA==e09d5ef40227227bc0761c5be7738da7");
  }

  ngOnInit(): void {
    this.sectionService.setSelectedSubSection('Argus');
    this.isLoading = true;
    this.activityNumber = this.route.snapshot.paramMap.get('id')!;
    if (!this.argusDataService.hasData(this.activityNumber)){
      this.userService.userData$.subscribe({
        next: (data) => {
        this.userData = data;
        if (this.userData)
          this.fetchData();
      },
      error: (error) => {        
        this.isLoading = false;
        console.error('Error in ngOnInit', error);
      },
      complete: () => { this.isLoading = false; } });
    } else {
      this.argusViewModel = this.argusDataService.getData(this.activityNumber);
      this.isLoading = false;
    }

    console.log('Argus Component Loaded:');
  }

  fetchData(): void {
    const cachedData = this.appraisalDataService.getRowData();
    
    const apiUrl = environment.argusApiConfig.baseUrl + "/api/v1/argus/" + this.activityNumber;
    //const apiUrl = 'assets/files/5500 Greenwood Plaza Boulevard.json';
    this.inputParams = {
      email: this.userData.email
    };

    this.http.get<any[]>(apiUrl, { params: this.inputParams }).subscribe({
      next: (data) => {
        this.argusResponse = data;
        this.getArgusData();
        this.argusViewModel = this.mapToArgusViewModel();
        this.argusDataService.setData(this.argusViewModel);
        this.isLoading = false;
      },
      error: (error) => {        
        this.argusViewModel = {Common: {ActivityNumber: this.activityNumber, ErrorMessages: [{message: "Something went wrong. Please check your Argus API access and try again.", messageType: 0, exception: error}]}} as Argus;        
        this.isLoading = false;
        console.error('Error fetching data:', error);
      },
      complete: () => { this.isLoading = false; }
    });
  }

  getArgusData(): void {
    let cashflowRowData: any[] = [];

    const uniqueMessages = Array.from(
      new Map(this.argusResponse?.logMessages.map((item: { message: string}) => [item.message, item])).values()
    ) as {message: string, messageType: string}[];

    uniqueMessages.filter((message: { messageType: string }) => message.messageType === "Error").forEach((errorMessage: any)=>{
      this.errorMessages.push(errorMessage);
    });    
    
    if (this.errorMessages.length == 0)
    {
      uniqueMessages.filter((message: { messageType: string }) => message.messageType === "Warning").forEach((warningMessage: any)=>{
        this.warningMessages.push(warningMessage);
      }); 
      this.model.yearlyNOI = this.filterCashflowLineItemByDescription("Net Operating Income");
      this.model.capitalExpenses = this.filterCashflowLineItemByDescription("Capital Expense");
      this.model.analysisLength = this.getDateDifference(this.argusResponse?.valuationAssumptionsResponseModel?.modelReferenceData?.analysisBegin, this.argusResponse?.valuationAssumptionsResponseModel?.modelReferenceData?.analysisEnd);
      this.model.marketInflationRates = this.filterInflationRates("Market Inflation Rate");
      this.model.expenseInflationRates = this.filterInflationRates("Expense Inflation Rate");      
      this.model.marketInflationRateRowData = this.getInflationRate(this.model?.marketInflationRates?.[0].rates);
      this.model.expenseInflationRateRowData = this.getInflationRate(this.model?.expenseInflationRates?.[0].rates);
      this.getLeaseExpiration();
      this.model.status = this.argusResponse?.propertyInformationResponseModel3?.entityModel?.propertyClassifications.filter((c: { classificationName: string, value: string }) => c.classificationName == 'Status')[0].value;               
      this.model.holdingPeriod = this.model?.analysisLength?.months > 0 ? this.model.analysisLength.years + 1 : this.model?.analysisLength?.years;   
      cashflowRowData.push(this.getCashFlowRow("Net Operating Income", this.model.yearlyNOI));
      cashflowRowData.push(this.getCashFlowRow("Capital Expense", this.model.capitalExpenses));
      this.model.cashflowRowData = cashflowRowData;
    }    
  }

  mapToArgusViewModel(): Argus {
    return {
      Common: { ActivityNumber: this.activityNumber, ErrorMessages: this.errorMessages, WarningMessages: this.warningMessages },
      PropertyOverview: {
         ExternalId: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.externalId,
         PortfolioName: this.argusResponse.portfolioName,
         ScenarioName: this.argusResponse.scenarioName,
         PropertyId: this.argusResponse?.propertyInformationResponseModel3?.trackingInfo.propertyAssetId,
         PropertyName: this.argusResponse?.propertyInformationResponseModel3?.entityModel.propertyName,
         PropertyType: this.getPropertyTypeByValue(this.argusResponse?.propertyInformationResponseModel3?.entityModel?.propertyType),
         Status: this.model.status
        },
      LocationInfo: {
        PropertyName: this.argusResponse?.propertyInformationResponseModel3?.entityModel.propertyName,
        Address: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.addressLine1,
        City: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.city,
        State: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.state,
        Zipcode: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.zip,
        Country: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.country
      },
      Analysis: {
        AnalysisBeginDate: this.argusResponse?.valuationAssumptionsResponseModel?.modelReferenceData?.analysisBegin,
        AnalysisEndDate: this.argusResponse?.valuationAssumptionsResponseModel?.modelReferenceData?.analysisEnd
      },
      ImprovementInfo: {
        BuildingArea: this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.buildingArea?.singleAmount,
        VacancyPercentage: this.argusResponse.generalVacancyResponseModel?.generalVacancy?.amount?.singleAmount ?? this.argusResponse.generalVacancyResponseModel?.generalVacancy?.amount?.detailAmount?.[0]?.amount
      },
      CashFlow: {
        WALE_Area: this.argusResponse?.kpiSummaryResponseModel?.kpiSummaryModel?.wale?.waleArea,
        WALE_Rent: this.argusResponse?.kpiSummaryResponseModel?.kpiSummaryModel?.wale?.waleRent,
        WALT: this.argusResponse?.gTTLeaseDataACs?.reduce((sum: any, lease: { marketRent: any; }) => sum + lease.marketRent, 0),
        CollectionLoss: this.argusResponse.creditLossResponseModel?.creditLoss?.amount?.singleAmount ??
        this.argusResponse.creditLossResponseModel?.creditLoss?.amount?.detailAmount?.[0]?.amount,
        NetOperatingIncome: {
          ColumnDefs: this.cashFlowColumnDefs,
          RowData: this.model.cashflowRowData
        },
        RentRollforWALT: {
          ColumnDefs: this.wALTColumnDefs,
          RowData: this.argusResponse.gTTLeaseDataACs
        }
      },
      Assumptions: {
        MarketInflationRate: {
          ColumnDefs: this.inflationRateColumnDefs,
          RowData: this.model?.marketInflationRateRowData
        },
        ExpenseInflationRate: {
          ColumnDefs: this.inflationRateColumnDefs,
          RowData: this.model?.expenseInflationRateRowData
        }
      },
      LeaseExpirations: {
        ColumnDefs: this.leaseExpirationColumnDefs,
        RowData: this.model?.expireRows
      },
      LeasingAssumptions: {
        ColumnDefs: this.leasingAssumptionsColumnDefs,
        RowData: this.argusResponse.gTTMarketLeaseDataACs
      },
      ContractMonthRent: {
        ColumnDefs: this.contractRentColumnDefs,
        RowData: this.argusResponse.gTTLeaseDataACs
      },
      ValuationSummary: {
        HoldingPeriod: this.model?.holdingPeriod ? this.model?.holdingPeriod : this.argusResponse?.yearlyNOI?.[0]?.amounts?.length,
        CapitalizationRate: this.argusResponse?.propertyResalesResponseModel?.resales?.[0]?.capRate?.singleAmount
      }
    }
  }

  filterCashflowLineItemByDescription(description: string): any[] {
    const resultLineItems =  this.argusResponse.resultLinesResponseModel2?.resultLineItems?.filter((cli: { lineItemDescription: string; }) => cli.lineItemDescription === description);
    if (resultLineItems?.length> 0 )
      return resultLineItems;
    else {
    const propertyFinancialResults = this.argusResponse.propertyFinancialResultsResponseModel?.resultLineItems?.filter((cli: { lineItemDescription: string; }) => cli.lineItemDescription === description);
    if (propertyFinancialResults?.length > 0) {
      propertyFinancialResults[0].amounts = this.sumEach12Items(propertyFinancialResults[0].amounts);
    }
      return propertyFinancialResults;
    }    
  };

  filterInflationRates(name: string): any[] {
    return this.argusResponse?.inflationsResponseModel?.inflationModel?.inflationProfiles?.filter((cli: { name: string; }) => cli.name === name);
  };

  getDateDifference(startDateString: string, endDateString: string): { years: number; months: number } {

    let startDate = new Date(startDateString);
    let endDate = new Date(endDateString);
    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    return { years, months };
  }

  sumEach12Items(arr: number[]): number[] {
    const result: number[] = [];

    for (let i = 0; i < arr.length; i += 12) {
      const chunk = arr.slice(i, i + 12);
      const sum = chunk.reduce((acc, val) => acc + val, 0);
      result.push(sum);
    }

    return result;
  }

  getPropertyTypeByValue(value: number): string | undefined {
    return PropertyType[value] as string | undefined;
  }

  getLeaseExpiration(): void {
    let expireRows: any[] = [];
    let expires: number = 0;
    let periodStart = new Date(this.argusResponse?.valuationAssumptionsResponseModel?.modelReferenceData?.analysisBegin);
    let periodEnd = new Date(periodStart);
    let expireRow: any = {};
    for (let year = 1; year <= 5; year++) {
      periodEnd.setFullYear(periodEnd.getFullYear() + 1);
      let expiry: number = 0;
      this.argusResponse.tenantSummaryResponseModel?.tenantsSummary?.forEach((x: { expiryDate: string; dateExpiryDate: Date }) => {
        x.dateExpiryDate = new Date(x.expiryDate);
      });
      let tenantSummaries = this.argusResponse.tenantSummaryResponseModel?.tenantsSummary
        .filter((x: { dateExpiryDate: Date; }) =>
          // Filter records based on ExpiryDate
          periodStart <= x.dateExpiryDate && periodEnd > x.dateExpiryDate
        );

      tenantSummaries?.forEach((x: any) => {
        const matchedLease = this.argusResponse.gTTLeaseDataACs.find(
          (y: { tenantId: any; }) => y.tenantId === x.tenantId
        );

        if (matchedLease) {

          const applicableAreas = matchedLease.areas.filter((z: { date: string, area: number }) => new Date(z.date) <= x.dateExpiryDate);

          const area = applicableAreas.sort((a: { date: string; }, b: { date: string }) => (new Date(b.date).getTime() - new Date(a.date).getTime()))[0]; // Get the most recent Area

          if (area) {
            expiry += area.area;
          }
        }
      }
      );
      expires += expiry;
      expireRow['year ' + String(year)] = expiry;
      periodStart = new Date(periodEnd);
    }
    expireRow['5yeartotal'] = expires;
    expireRows.push(expireRow);
    this.model.expireRows = expireRows;
    this.model.leaseExpires = expires / this.argusResponse?.propertyInformationResponseModel3?.entityModel?.details?.buildingArea?.singleAmount;
  }

  getInflationRate(rates: [{ index: number, amount: number }]): any[] {
    if ((rates === null || undefined) || rates?.length == null) { return []};
    let inflationRateRowData: any[] = [];
    let analysisBegin = new Date(this.argusResponse?.valuationAssumptionsResponseModel?.modelReferenceData?.analysisBegin);
    let startYear = analysisBegin;
    startYear.setMonth(startYear.getMonth() + 11);
    let noOfMonths = rates[rates.length - 1].index;
    let noOfYears = Math.ceil(noOfMonths / 12);
    //Column definition prepare
    if (this.inflationRateColumnDefs.length == 1)
    {
      for (let year = 0; year <= noOfYears; year++) {
        this.inflationRateColumnDefs.push(
          {
            headerName: formatDate(startYear, 'MMM yyyy', 'en-US'), field: 'year ' + String(year), minWidth: 70, maxWidth: 120, headerClass: 'multi-line-header', valueFormatter: (params: { value: any; }) => {
              const value = Number(params.value);
              if (isNaN(value)) return '';
              return value + '%';
            }, cellClass: 'ag-right-aligned-cell',
            autoHeight: true,
            wrapHeaderText: true
          }
        );
        startYear.setMonth(startYear.getMonth() + 12);
      }
    }

    startYear = analysisBegin;
    //Row defnition here     
    for (let month = 0; month < 12; month++) {
      let rateRow: any = {};
      startYear.setMonth(startYear.getMonth() + 1);
      rateRow['month'] = formatDate(startYear, 'MMM', 'en-US');

      for (let year = 0; year <= noOfYears; year++) {
        let rate = rates.find(
          (x: { index: number; amount: number }) => (Math.floor(x.index / 12) === year && Math.ceil(x.index % 12) === month)
        );
        if (rate) {
          rateRow['year ' + String(year)] = rate.amount * 100;
        } else {
          rateRow['year ' + String(year)] = 0;
        }
      }
      inflationRateRowData.push(rateRow);
    }
    return inflationRateRowData;
  }

  getCashFlowRow(cashFlowName: string, cashFlowYearly: any[]): any {
    let cashflowRowData: any = {};

    if (cashFlowYearly?.length > 0 )
    {
      if (this.cashFlowColumnDefs.length === 1) {        
        //Column definition prepare
        for (let year = 1; year <= cashFlowYearly[0].amounts.length; year++) {
          this.cashFlowColumnDefs.push(
            {
              headerName: 'Year ' + String(year), field: 'year ' + String(year), minWidth: 70, maxWidth: 120, headerClass: 'multi-line-header', valueFormatter: (params: { value: any; }) => {
                const val = Number(params.value);
                if (isNaN(val)) return '';
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0
                }).format(Math.round(val));
              }, cellClass: 'ag-right-aligned-cell'
            }
          );
        }   
      }

    //Row defnition here  
    cashflowRowData['cashflow'] = cashFlowName;
    for (let year = 1; year <= cashFlowYearly[0].amounts.length; year++) {
      cashflowRowData['year ' + String(year)] = Number(cashFlowYearly[0].amounts[year - 1]);
    }
  }
    return cashflowRowData;
  
  }
}

enum PropertyType {
  "Office" = 0,
  "Retail" = 1,
  "Industrial" = 2,
  "Mixed-Use (Office/Retail)" = 3,
  "Mixed-Use (Office/Industrial)" = 4,
  "Mixed-Use (Office/Multifamily)" = 5,
  "Mixed-Use (Retail/Multifamily)" = 6,
  "Multifamily" = 7,
  "Hotel" = 8
}

enum LeaseStatus {
  "Contract" = 0,
  "Speculative" = 1,
  "Option" = 2,
  "ContractRenewal" = 3,
  "MonthToMonth" = 4,
  "Holdover" = 5,
  "Custom" = -1
}

